import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { scdMatchesFilter, scdSecondaryLabel, groupScds, catalogNameFromRxNorm, normalizeStrengthUnits } from './rxNormDisplay';

const API_BASE = `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/medication-catalog`;
const apiError = (error, fallback) => error.response?.data?.error || fallback;

const STEPS = {
  CHOOSE_PATH: 'choose-path',
  PICK_MEDICATION: 'pick-medication',
  CHOOSE_PRESENTATION: 'choose-presentation',
  PICK_EXISTING_PRESENTATION: 'pick-existing-presentation',
  SEARCH_RXNORM: 'search-rxnorm',
  PICK_SCD: 'pick-scd',
  REVIEW_FIELDS: 'review-fields',
  OVERRIDE_MANUAL: 'override-manual',
  CONFIRM: 'confirm',
};

const emptyParsed = {
  rxcui: '',
  rxnorm_name: '',
  strength: '',
  dosage_form: '',
  route: '',
  dose_form: '',
};

// Zoho names are usually "<product> <strength> <form>". Keep only the first word.
const guessedCatalogName = (raw) => String(raw || '').trim().split(/\s+/)[0] || '';

const LinkItemWizard = ({
  item,
  canOverride,
  headers,
  routes,
  dosageForms,
  onCancel,
  onSaved,
}) => {
  const [step, setStep] = useState(STEPS.CHOOSE_PATH);
  const [path, setPath] = useState(null);
  const [medications, setMedications] = useState([]);
  const [medicationFilter, setMedicationFilter] = useState('');
  const [medication, setMedication] = useState(null);
  const [presentation, setPresentation] = useState(null);
  const [query, setQuery] = useState(item?.item_name || '');
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [ingredient, setIngredient] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [scds, setScds] = useState([]);
  const [scdFilter, setScdFilter] = useState('');
  const [selectedScd, setSelectedScd] = useState('');
  const [parsed, setParsed] = useState(emptyParsed);
  const [notes, setNotes] = useState('');
  const [brandName, setBrandName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [emptyCatalog, setEmptyCatalog] = useState(false);
  const [catalogReady, setCatalogReady] = useState(false);
  const [overrideFromScd, setOverrideFromScd] = useState(false);
  const errorRef = useRef(null);

  const existingScdIds = useMemo(
    () => new Set((medication?.MedicationPresentations || []).map((row) => String(row.rxcui || '')).filter(Boolean)),
    [medication]
  );
  const activePresentations = useMemo(
    () => (medication?.MedicationPresentations || []).filter((row) => row.is_active),
    [medication]
  );

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [error]);

  const resetFromMedication = () => {
    setPresentation(null);
    setIngredient(null);
    setScds([]);
    setScdFilter('');
    setSelectedScd('');
    setParsed({
      ...emptyParsed,
      dosage_form: dosageForms[0] || 'Tablet',
      route: routes[0] || 'PO',
    });
    setNotes('');
    setSearchResult(null);
  };

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE}/medications`, { headers })
      .then((response) => {
        if (cancelled) return;
        const rows = (response.data || []).filter((row) => row.is_active);
        setMedications(rows);
        setEmptyCatalog(!rows.length);
        if (!rows.length) {
          setPath('new');
          setStep(STEPS.SEARCH_RXNORM);
        }
        setCatalogReady(true);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(apiError(err, 'Could not load medications for linking.'));
          setCatalogReady(true);
        }
      });
    return () => { cancelled = true; };
  }, [headers]);

  const loadScds = useCallback(async (rxcui) => {
    const response = await axios.get(`${API_BASE}/rxnorm/scds`, {
      headers,
      params: { rxcui },
    });
    setScds(response.data.scds || []);
    if (response.data.ingredient) setIngredient(response.data.ingredient);
  }, [headers]);

  const parseScd = useCallback(async (rxcui) => {
    const response = await axios.post(
      `${API_BASE}/rxnorm/presentations`,
      { scd_rxcuis: [rxcui] },
      { headers }
    );
    const row = response.data?.[0];
    if (!row) throw new Error('Could not read that RxNorm presentation.');
    setParsed({
      rxcui: row.rxcui,
      rxnorm_name: row.rxnorm_name,
      strength: normalizeStrengthUnits(row.strength || ''),
      dosage_form: row.dosage_form || dosageForms[0] || 'Tablet',
      route: row.route || (row.dosage_form === 'Injection' ? 'IV' : 'PO'),
      dose_form: row.dose_form || '',
      needs_review: row.needs_review,
    });
    return row;
  }, [dosageForms, headers]);

  const runSearch = useCallback(async (event, nextQuery = query) => {
    event?.preventDefault();
    const searchTerm = String(nextQuery || '').trim();
    if (searchTerm.length < 3) return;
    setError('');
    setSearching(true);
    try {
      const response = await axios.get(`${API_BASE}/rxnorm/search`, {
        headers,
        params: { query: searchTerm },
      });
      setSearchResult(response.data);
      const ingredients = response.data.ingredients || [];
      if (ingredients.length === 1) {
        setIngredient(ingredients[0]);
      }
    } catch (err) {
      setSearchResult({ ingredients: [], scds: [] });
      setError(apiError(err, 'RxNorm search failed.'));
    } finally {
      setSearching(false);
    }
  }, [headers, query]);

  useEffect(() => {
    if (step !== STEPS.SEARCH_RXNORM) return;
    const searchTerm = query.trim();
    if (searchTerm.length < 3) return;
    const timer = setTimeout(() => runSearch(null, searchTerm), 400);
    return () => clearTimeout(timer);
  }, [query, step, runSearch]);

  const chooseExistingPath = () => {
    setError('');
    setPath('existing');
    setDisplayName('');
    setIngredient(null);
    setOverrideFromScd(false);
    setMedication(null);
    resetFromMedication();
    setStep(STEPS.PICK_MEDICATION);
  };

  const chooseNewPath = () => {
    setError('');
    setPath('new');
    setMedication(null);
    setOverrideFromScd(false);
    resetFromMedication();
    setQuery(item?.item_name || '');
    setDisplayName('');
    setStep(STEPS.SEARCH_RXNORM);
  };

  const pickMedication = async (row) => {
    setError('');
    setBusy(true);
    try {
      const response = await axios.get(`${API_BASE}/medications/${row.medication_id}`, { headers });
      const detail = response.data;
      setMedication(detail);
      resetFromMedication();
      const listed = (detail.MedicationPresentations || []).filter((pres) => pres.is_active);
      if (listed.length) {
        setStep(STEPS.CHOOSE_PRESENTATION);
      } else if (detail.rxcui) {
        await loadScds(detail.rxcui);
        setStep(STEPS.PICK_SCD);
      } else {
        setQuery(detail.name || item?.item_name || '');
        setStep(STEPS.SEARCH_RXNORM);
      }
    } catch (err) {
      setError(apiError(err, 'Could not load that medication.'));
    } finally {
      setBusy(false);
    }
  };

  const pickIngredient = async (itemRow) => {
    setError('');
    setBusy(true);
    try {
      setIngredient(itemRow);
      if (path === 'new') {
        setDisplayName(catalogNameFromRxNorm(itemRow.name));
      }
      if (searchResult?.scds?.length && (searchResult.ingredients || []).length === 1) {
        setScds(searchResult.scds);
      } else {
        await loadScds(itemRow.rxcui);
      }
      setSelectedScd('');
      setParsed({
        ...emptyParsed,
        dosage_form: dosageForms[0] || 'Tablet',
        route: routes[0] || 'PO',
      });
      setStep(STEPS.PICK_SCD);
    } catch (err) {
      setError(apiError(err, 'Could not load presentations for this drug.'));
    } finally {
      setBusy(false);
    }
  };

  const chooseListedPresentation = () => {
    setError('');
    setPresentation(null);
    setSelectedScd('');
    setStep(STEPS.PICK_EXISTING_PRESENTATION);
  };

  const chooseNewPresentation = async () => {
    setError('');
    setPresentation(null);
    setBusy(true);
    try {
      if (medication?.rxcui) {
        await loadScds(medication.rxcui);
        setStep(STEPS.PICK_SCD);
      } else {
        setQuery(medication?.name || item?.item_name || '');
        setSearchResult(null);
        setStep(STEPS.SEARCH_RXNORM);
      }
    } catch (err) {
      setError(apiError(err, 'Could not load RxNorm presentations.'));
    } finally {
      setBusy(false);
    }
  };

  const pickExistingPresentation = (row) => {
    setError('');
    setPresentation(row);
    setParsed({
      rxcui: row.rxcui || '',
      rxnorm_name: row.rxnorm_name || '',
      strength: row.strength,
      dosage_form: row.dosage_form,
      route: row.route,
      dose_form: '',
    });
    setStep(STEPS.CONFIRM);
  };

  const pickOneScd = async (rxcui) => {
    const id = String(rxcui);
    if (existingScdIds.has(id)) return;
    setError('');
    setBusy(true);
    try {
      setSelectedScd(id);
      await parseScd(id);
      setStep(STEPS.REVIEW_FIELDS);
    } catch (err) {
      setError(apiError(err, 'Could not read that RxNorm presentation.'));
    } finally {
      setBusy(false);
    }
  };

  const goReviewToConfirm = () => {
    if (!parsed.strength || !parsed.dosage_form || !parsed.route) {
      setError('Fill strength, form, and route.');
      return;
    }
    if (path === 'new' && !displayName.trim() && !ingredient?.name) {
      setError('Add a name to show in the catalog.');
      return;
    }
    setError('');
    setStep(STEPS.CONFIRM);
  };

  const startOverride = () => {
    setError('');
    setOverrideFromScd(false);
    setIngredient(null);
    if (path === 'new') {
      setDisplayName((current) => (
        current || guessedCatalogName(item?.item_name) || guessedCatalogName(query) || ''
      ));
    }
    setParsed({
      ...emptyParsed,
      dosage_form: dosageForms[0] || 'Tablet',
      route: routes[0] || 'PO',
    });
    setSelectedScd('');
    setStep(STEPS.OVERRIDE_MANUAL);
  };

  const startManualForm = () => {
    setError('');
    setOverrideFromScd(true);
    setSelectedScd('');
    setParsed({
      ...emptyParsed,
      dosage_form: dosageForms[0] || 'Tablet',
      route: routes[0] || 'PO',
    });
    setStep(STEPS.OVERRIDE_MANUAL);
  };

  const goOverrideToConfirm = () => {
    if (path === 'new' && !displayName.trim()) {
      setError('Add a medication name.');
      return;
    }
    if (!parsed.strength || !parsed.dosage_form || !parsed.route) {
      setError('Fill strength, form, and route.');
      return;
    }
    if (!notes.trim()) {
      setError('Add a short note explaining why this is not in RxNorm.');
      return;
    }
    setError('');
    setStep(STEPS.CONFIRM);
  };

  const goBack = () => {
    setError('');
    if (step === STEPS.PICK_MEDICATION) {
      setMedication(null);
      setPath(null);
      setStep(STEPS.CHOOSE_PATH);
      return;
    }
    if (step === STEPS.CHOOSE_PRESENTATION) {
      resetFromMedication();
      setMedication(null);
      setStep(STEPS.PICK_MEDICATION);
      return;
    }
    if (step === STEPS.PICK_EXISTING_PRESENTATION) {
      setPresentation(null);
      setStep(STEPS.CHOOSE_PRESENTATION);
      return;
    }
    if (step === STEPS.SEARCH_RXNORM) {
      setSearchResult(null);
      setIngredient(null);
      if (path === 'existing' && medication) {
        if (activePresentations.length) setStep(STEPS.CHOOSE_PRESENTATION);
        else {
          setMedication(null);
          setStep(STEPS.PICK_MEDICATION);
        }
      } else if (emptyCatalog) {
        onCancel();
      } else {
        setPath(null);
        setStep(STEPS.CHOOSE_PATH);
      }
      return;
    }
    if (step === STEPS.PICK_SCD) {
      setSelectedScd('');
      if (path === 'existing' && medication?.rxcui && activePresentations.length) {
        setStep(STEPS.CHOOSE_PRESENTATION);
      } else if (path === 'existing' && medication?.rxcui) {
        setMedication(null);
        setStep(STEPS.PICK_MEDICATION);
      } else {
        setStep(STEPS.SEARCH_RXNORM);
      }
      return;
    }
    if (step === STEPS.REVIEW_FIELDS) {
      setStep(STEPS.PICK_SCD);
      return;
    }
    if (step === STEPS.OVERRIDE_MANUAL) {
      if (overrideFromScd) setStep(STEPS.PICK_SCD);
      else setStep(STEPS.SEARCH_RXNORM);
      return;
    }
    if (step === STEPS.CONFIRM) {
      if (presentation) {
        setStep(STEPS.PICK_EXISTING_PRESENTATION);
      } else if (selectedScd) {
        setStep(STEPS.REVIEW_FIELDS);
      } else {
        setStep(STEPS.OVERRIDE_MANUAL);
      }
    }
  };

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const isOverride = Boolean(!presentation && !parsed.rxcui);
      const payload = {
        item_id: item.item_id,
        brand_name: brandName || null,
        is_preferred: true,
        notes: notes || null,
        rxnorm_override: isOverride && !ingredient?.rxcui,
      };
      if (path === 'existing' && medication) {
        payload.medication_id = medication.medication_id;
      } else {
        payload.medication_name = displayName.trim()
          || catalogNameFromRxNorm(ingredient?.name)
          || guessedCatalogName(item?.item_name)
          || query;
        payload.medication_rxcui = ingredient?.rxcui || null;
      }
      if (presentation) {
        payload.presentation_id = presentation.presentation_id;
      } else {
        payload.strength = normalizeStrengthUnits(parsed.strength);
        payload.dosage_form = parsed.dosage_form;
        payload.route = parsed.route;
        payload.scd_rxcui = parsed.rxcui || selectedScd || null;
        payload.presentation_rxcui = parsed.rxcui || selectedScd || null;
        payload.presentation_rxnorm_name = parsed.rxnorm_name || null;
        payload.presentation_rxnorm_override = isOverride;
      }
      const response = await axios.post(`${API_BASE}/link-item`, payload, { headers });
      await onSaved(response.data);
    } catch (err) {
      setError(apiError(err, 'Could not add this item to the catalog.'));
    } finally {
      setBusy(false);
    }
  };

  const filteredMedications = medications.filter((row) => {
    if (!medicationFilter.trim()) return true;
    return row.name.toLowerCase().includes(medicationFilter.trim().toLowerCase());
  });
  const filteredScds = scds.filter((scd) => scdMatchesFilter(scd, scdFilter));
  const groupedScds = groupScds(filteredScds);
  const noMatch = searchResult && !searchResult.ingredients?.length && !searching;
  const catalogName = path === 'existing'
    ? (medication?.name || '')
    : (displayName || catalogNameFromRxNorm(ingredient?.name) || guessedCatalogName(item?.item_name) || query);
  const summary = `Zoho item "${item.item_name}" will be saved as a product of ${catalogName}, ${parsed.strength} ${parsed.dosage_form} ${parsed.route}.`;

  const actions = (nextLabel, nextAction, nextDisabled) => (
    <div className="medication-catalog-modal-actions">
      <button type="button" className="medication-catalog-btn" onClick={step === STEPS.CHOOSE_PATH ? onCancel : goBack}>
        {step === STEPS.CHOOSE_PATH ? 'Cancel' : 'Back'}
      </button>
      {nextAction && (
        <button type="button" className="medication-catalog-btn primary" onClick={nextAction} disabled={busy || nextDisabled}>
          {busy ? 'Please wait...' : nextLabel}
        </button>
      )}
    </div>
  );

  return (
    <div className="medication-catalog-modal-backdrop" onClick={onCancel}>
      <div
        className="medication-catalog-modal medication-catalog-form medication-catalog-wizard"
        onClick={(event) => event.stopPropagation()}
      >
        <h3>Add to catalog</h3>
        <p className="medication-catalog-step-copy">
          Adding: <strong>{item.item_name}</strong>
        </p>
        {error && (
          <div ref={errorRef} className="medication-catalog-status error medication-catalog-modal-error">
            {error}
          </div>
        )}

        {!catalogReady && (
          <div className="medication-catalog-empty">Loading...</div>
        )}

        {catalogReady && step === STEPS.CHOOSE_PATH && (
          <>
            <p className="medication-catalog-step-copy">Is this drug already in the catalog, or is it new?</p>
            <div className="medication-catalog-choice-grid">
              <button type="button" className="medication-catalog-choice" onClick={chooseExistingPath} disabled={!medications.length}>
                <span className="medication-catalog-choice-title">Already in the catalog</span>
                <span className="medication-catalog-choice-meta">Link this Zoho item to a drug you added before.</span>
              </button>
              <button type="button" className="medication-catalog-choice" onClick={chooseNewPath}>
                <span className="medication-catalog-choice-title">This is a new drug</span>
                <span className="medication-catalog-choice-meta">Search RxNorm, then pick the strength this item is.</span>
              </button>
            </div>
            {actions(null)}
          </>
        )}

        {step === STEPS.PICK_MEDICATION && (
          <>
            <p className="medication-catalog-step-copy">Choose the catalog drug this Zoho item belongs to.</p>
            <label>
              Find a drug
              <input
                type="search"
                placeholder="Paracetamol..."
                value={medicationFilter}
                onChange={(event) => setMedicationFilter(event.target.value)}
              />
            </label>
            <div className="rxnorm-result-list">
              {filteredMedications.length === 0 && (
                <div className="medication-catalog-empty">No catalog drugs match this search.</div>
              )}
              {filteredMedications.map((row) => (
                <button
                  key={row.medication_id}
                  type="button"
                  className="medication-catalog-row"
                  onClick={() => pickMedication(row)}
                  disabled={busy}
                >
                  <span className="medication-catalog-row-title">{row.name}</span>
                  <span className="medication-catalog-row-meta">
                    {row.presentation_count || 0} presentation{(row.presentation_count || 0) === 1 ? '' : 's'}
                    {row.rxcui ? ` · RxCUI ${row.rxcui}` : ''}
                  </span>
                </button>
              ))}
            </div>
            {actions(null)}
          </>
        )}

        {step === STEPS.CHOOSE_PRESENTATION && (
          <>
            <p className="medication-catalog-step-copy">
              Is this Zoho item the same strength as one already listed for <strong>{medication?.name}</strong>?
            </p>
            <div className="medication-catalog-choice-grid">
              <button type="button" className="medication-catalog-choice" onClick={chooseListedPresentation}>
                <span className="medication-catalog-choice-title">Use a listed strength</span>
                <span className="medication-catalog-choice-meta">This item is one of the strengths already in the catalog.</span>
              </button>
              <button type="button" className="medication-catalog-choice" onClick={chooseNewPresentation} disabled={busy}>
                <span className="medication-catalog-choice-title">Add a new strength</span>
                <span className="medication-catalog-choice-meta">This item is a strength that is not listed yet.</span>
              </button>
            </div>
            {actions(null)}
          </>
        )}

        {step === STEPS.PICK_EXISTING_PRESENTATION && (
          <>
            <p className="medication-catalog-step-copy">Which listed strength is this Zoho item?</p>
            <div className="medication-catalog-choice-grid">
              {activePresentations.map((row) => (
                <button
                  key={row.presentation_id}
                  type="button"
                  className={`medication-catalog-choice ${presentation?.presentation_id === row.presentation_id ? 'selected' : ''}`}
                  onClick={() => pickExistingPresentation(row)}
                >
                  <span className="medication-catalog-choice-title">{row.label}</span>
                  {row.rxcui ? <span className="medication-catalog-choice-meta">RxCUI {row.rxcui}</span> : null}
                </button>
              ))}
            </div>
            {actions(null)}
          </>
        )}

        {step === STEPS.SEARCH_RXNORM && (
          <form onSubmit={(event) => runSearch(event, query)}>
            {emptyCatalog && (
              <p className="medication-catalog-hint" style={{ padding: 0 }}>
                The catalog is empty, so this will be added as a new drug.
              </p>
            )}
            {path === 'existing' && medication && !medication.rxcui && (
              <p className="medication-catalog-step-copy">
                <strong>{medication.name}</strong> is not linked to RxNorm yet. Search for a match, then pick the strength of this Zoho item.
              </p>
            )}
            {path === 'new' && (
              <p className="medication-catalog-step-copy">Search RxNorm for the generic drug name.</p>
            )}
            <label>
              Search RxNorm
              <input
                type="search"
                required
                minLength={3}
                placeholder="Paracetamol, ibuprofen..."
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </label>
            <div className="medication-catalog-modal-actions">
              <button type="button" className="medication-catalog-btn" onClick={goBack}>Back</button>
              <button type="submit" className="medication-catalog-btn primary" disabled={searching}>
                {searching ? 'Searching...' : 'Search'}
              </button>
            </div>
            {searchResult?.ingredients?.length > 0 && (
              <div className="rxnorm-result-list">
                <p className="medication-catalog-hint" style={{ padding: '12px 0 4px' }}>
                  {searchResult.approximated ? 'Closest RxNorm matches:' : 'Select the generic drug:'}
                </p>
                {searchResult.ingredients.map((row) => (
                  <button
                    key={row.rxcui}
                    type="button"
                    className={`medication-catalog-row ${ingredient?.rxcui === row.rxcui ? 'selected' : ''}`}
                    onClick={() => pickIngredient(row)}
                    disabled={busy}
                  >
                    <span className="medication-catalog-row-title">{row.name}</span>
                    <span className="medication-catalog-row-meta">RxCUI {row.rxcui} · {row.tty}</span>
                  </button>
                ))}
              </div>
            )}
            {noMatch && (
              <div className="medication-catalog-empty">
                Nothing in RxNorm matched this name.
                {canOverride ? (
                  <div style={{ marginTop: 12 }}>
                    <button type="button" className="medication-catalog-btn" onClick={startOverride}>
                      Not in RxNorm
                    </button>
                  </div>
                ) : (
                  <p>A supervisor with RxNorm override permission is needed to add it anyway.</p>
                )}
              </div>
            )}
          </form>
        )}

        {step === STEPS.PICK_SCD && (
          <>
            <p className="medication-catalog-step-copy">
              Tick the one strength and form this Zoho item is.
              {ingredient ? <> RxNorm name: <strong>{ingredient.name}</strong>.</> : null}
            </p>
            {path === 'new' && (
              <label>
                Name to show in the catalog
                <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
              </label>
            )}
            <label>
              Filter presentations
              <input
                type="search"
                placeholder="syrup, 100 mg/5 ml, tablet..."
                value={scdFilter}
                onChange={(event) => setScdFilter(event.target.value)}
              />
            </label>
            <p className="medication-catalog-hint" style={{ padding: 0 }}>
              Liquids are often named Oral Suspension, not syrup.
            </p>
            <div className="rxnorm-scd-list">
              {groupedScds.length === 0 && (
                <div className="medication-catalog-empty">
                  No presentations match this filter. Try syrup, suspension, or 100 mg.
                </div>
              )}
              {groupedScds.map(([doseForm, rows]) => (
                <div key={doseForm} className="rxnorm-scd-group">
                  <h4>{doseForm}</h4>
                  {rows.map((scd) => {
                    const already = existingScdIds.has(String(scd.rxcui));
                    const secondary = scdSecondaryLabel(scd);
                    return (
                      <label key={scd.rxcui} className={`checkbox-row rxnorm-scd-item ${already ? 'inactive' : ''}`}>
                        <span className="rxnorm-scd-item-line">
                          <input
                            type="radio"
                            name="link-scd"
                            disabled={already || busy}
                            checked={!already && selectedScd === String(scd.rxcui)}
                            onChange={() => pickOneScd(scd.rxcui)}
                          />
                          <span className="rxnorm-scd-item-name">
                            {scd.name}
                            {already ? ' (already in catalog)' : ''}
                          </span>
                        </span>
                        {secondary ? (
                          <span className="medication-catalog-row-meta rxnorm-scd-item-meta">{secondary}</span>
                        ) : null}
                      </label>
                    );
                  })}
                </div>
              ))}
            </div>
            {canOverride ? (
              <button type="button" className="medication-catalog-choice" onClick={startManualForm}>
                <span className="medication-catalog-choice-title">This form is not listed</span>
                <span className="medication-catalog-choice-meta">
                  The drug is in RxNorm, but this strength or form is not. Add it by hand.
                </span>
              </button>
            ) : (
              <p className="medication-catalog-hint" style={{ padding: '8px 0 0' }}>
                If the form you need is not listed, a supervisor with RxNorm override permission can add it by hand.
              </p>
            )}
            {actions(null)}
          </>
        )}

        {step === STEPS.REVIEW_FIELDS && (
          <>
            <p className="medication-catalog-step-copy">Check strength, form, and route. Confirm injection routes (IV vs IM).</p>
            <div className="rxnorm-review-card">
              <div className="medication-catalog-row-meta">{parsed.rxnorm_name} · RxCUI {parsed.rxcui}</div>
              {parsed.needs_review && <span className="medication-catalog-badge warn">Check form/route</span>}
              {/suspension|solution/i.test(parsed.dose_form || '') && (
                <div className="medication-catalog-row-meta">Hospital bottles are often labeled syrup. Change the form if needed.</div>
              )}
              <label>
                Strength
                <input
                  value={parsed.strength}
                  onChange={(event) => setParsed((prev) => ({ ...prev, strength: event.target.value }))}
                />
              </label>
              <label>
                Dosage form
                <select
                  value={parsed.dosage_form}
                  onChange={(event) => setParsed((prev) => ({ ...prev, dosage_form: event.target.value }))}
                >
                  {dosageForms.map((form) => <option key={form} value={form}>{form}</option>)}
                </select>
              </label>
              <label>
                Route
                <select
                  value={parsed.route}
                  onChange={(event) => setParsed((prev) => ({ ...prev, route: event.target.value }))}
                >
                  {routes.map((route) => <option key={route} value={route}>{route}</option>)}
                </select>
              </label>
            </div>
            {actions('Next', goReviewToConfirm, !parsed.strength || !parsed.dosage_form || !parsed.route)}
          </>
        )}

        {step === STEPS.OVERRIDE_MANUAL && (
          <>
            <p className="medication-catalog-step-copy">
              {overrideFromScd
                ? 'The drug was found in RxNorm, but this strength and form will be saved without an RxNorm presentation code.'
                : 'This will be saved without an RxNorm code. Only use this when the drug is not in RxNorm.'}
            </p>
            {path === 'new' && (
              <label>
                Medication name
                <input
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                />
              </label>
            )}
            <label>
              Strength
              <input
                placeholder="100 mg/5 mL"
                value={parsed.strength}
                onChange={(event) => setParsed((prev) => ({ ...prev, strength: event.target.value }))}
              />
            </label>
            <label>
              Dosage form
              <select
                value={parsed.dosage_form}
                onChange={(event) => setParsed((prev) => ({ ...prev, dosage_form: event.target.value }))}
              >
                {dosageForms.map((form) => <option key={form} value={form}>{form}</option>)}
              </select>
            </label>
            <label>
              Route
              <select
                value={parsed.route}
                onChange={(event) => setParsed((prev) => ({ ...prev, route: event.target.value }))}
              >
                {routes.map((route) => <option key={route} value={route}>{route}</option>)}
              </select>
            </label>
            <label>
              {overrideFromScd ? 'Why is this form not in RxNorm?' : 'Why is this not in RxNorm?'}
              <textarea rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} />
            </label>
            {actions(
              'Next',
              goOverrideToConfirm,
              !canOverride
                || (path === 'new' && !displayName.trim())
                || !parsed.strength
                || !parsed.dosage_form
                || !parsed.route
                || !notes.trim()
            )}
          </>
        )}

        {step === STEPS.CONFIRM && (
          <>
            <p className="medication-catalog-step-copy">{summary}</p>
            <label>
              Brand name (optional)
              <input
                placeholder="Leave blank for generic"
                value={brandName}
                onChange={(event) => setBrandName(event.target.value)}
              />
            </label>
            {actions('Save to catalog', save, false)}
          </>
        )}
      </div>
    </div>
  );
};

export default LinkItemWizard;
